import {
  isAttendanceChatbotEnabled,
  isFaqChatbotEnabled,
  isLyceum,
  isVersiEnabled,
} from 'common/HelperFunctions'
import {calculateErrorMessage} from 'common/helpers'
import React, {useEffect, useState} from 'react'
import {useLocation} from 'react-router'

import AssistantChatbotPage from './modes/AssistantChatbotPage'
import {getChatSurface, isApplicantChatPath} from './modes/assistant/chatSurface'
import {useChatViewer} from './modes/assistant/useChatViewer'

const HIGHLIGHT_LAST_SHOWN_KEY = 'chatbot-highlight-last-shown'
/** Matches the first-open pulse feel — a few cycles, then quiet. */
const DAILY_HIGHLIGHT_DURATION_MS = 6000

/** Recurring (daily) launcher highlight, replacing the once-ever intro pulse semantics. */
function shouldHighlightToday(): boolean {
  try {
    return localStorage.getItem(HIGHLIGHT_LAST_SHOWN_KEY) !== new Date().toDateString()
  } catch (e) {
    calculateErrorMessage(e)
    return false
  }
}

/**
 * Single mount point for the floating "Versi AI Assistant" widget. Decides which
 * backend boards this user gets (attendance: hostname/tenant check; knowledge base:
 * `knowledge_base` permission; both only for ADMIN / TEACHER / STAFF via `isVersiEnabled`)
 * and renders nothing when neither applies, so students and parents never see it. Routing
 * between the boards happens per message inside the page — there is one tab.
 *
 * It also renders nothing for anyone else who isn't ADMIN / TEACHER / STAFF, so visitors and
 * applicants on `/admissions/*` don't get it either — except on Lyceum, where signed-in students
 * get the admission assistant on the admission form and the applicant dashboard only. Staff get the
 * general surface everywhere.
 */
const ChatbotHost: React.FC = () => {
  const {pathname} = useLocation()
  const viewer = useChatViewer()
  const isApplicantSurface = getChatSurface(pathname, viewer) === 'admission_applicant'
  const attendanceEnabled = !isApplicantSurface && isAttendanceChatbotEnabled()
  const faqEnabled = !isApplicantSurface && isFaqChatbotEnabled()

  const [dailyHighlight, setDailyHighlight] = useState(() => shouldHighlightToday())

  useEffect(() => {
    if (!dailyHighlight) return
    try {
      localStorage.setItem(HIGHLIGHT_LAST_SHOWN_KEY, new Date().toDateString())
    } catch (e) {
      calculateErrorMessage(e)
    }
    const timer = window.setTimeout(() => setDailyHighlight(false), DAILY_HIGHLIGHT_DURATION_MS)
    return () => window.clearTimeout(timer)
  }, [dailyHighlight])

  // Versi is for ADMIN / TEACHER / STAFF only — students, parents, applicants and signed-out
  // visitors (including on the /admissions pages) never see it. Lyceum is the exception: its
  // student applicants get the admission assistant on the admission form and the applicant
  // dashboard. Parents and signed-out visitors don't.
  const applicantChatEnabled =
    isApplicantSurface &&
    Boolean(viewer.isLoggedIn) &&
    viewer.userType === 'STUDENT' &&
    isLyceum() &&
    isApplicantChatPath(pathname)
  if (!applicantChatEnabled && !isVersiEnabled()) return null
  if (!isApplicantSurface && !attendanceEnabled && !faqEnabled) return null

  return (
    <AssistantChatbotPage
      attendanceEnabled={attendanceEnabled}
      faqEnabled={faqEnabled}
      dailyHighlight={dailyHighlight}
    />
  )
}

export default ChatbotHost
